import { characterStyle, type CharacterStyle } from '../core/characters';
import { MAX_PLAYERS } from '../core/players';
import { readRoomMessage } from './protocol';

export type PartyProfileMember = { id: number; color: number };

export type PartyProfiles = {
  /** Seed new members and forget departed players using the relay's current roster. */
  syncRoster(members: readonly PartyProfileMember[]): boolean;
  /** The host can update only its own look through this local setter. */
  setOwn(value: unknown): boolean;
  /** Guest identity comes exclusively from the authenticated relay envelope. */
  receive(from: number, raw: unknown): boolean;
  /** A fresh public value suitable for lobby/start messages; no mutable references escape. */
  get(peerId: number, fallbackColor?: number): CharacterStyle;
};

function sameStyle(a: CharacterStyle, b: CharacterStyle): boolean {
  return a.personality === b.personality && a.accessory === b.accessory &&
    a.marking === b.marking && a.skin === b.skin;
}

/** Host-owned cosmetics survive matches but never enter deterministic world state. */
export function createPartyProfiles(hostId: number, ownCharacter?: unknown): PartyProfiles {
  if (!Number.isSafeInteger(hostId) || hostId < 1) throw new Error('A party profile needs a valid host.');
  let members: PartyProfileMember[] = [{ id: hostId, color: 0 }];
  const profiles = new Map<number, CharacterStyle>([[hostId, characterStyle(ownCharacter)]]);

  function update(peerId: number, value: unknown): boolean {
    const member = members.find((candidate) => candidate.id === peerId);
    if (!member) return false;
    const next = characterStyle(value, member.color);
    const previous = profiles.get(peerId);
    if (previous && sameStyle(previous, next)) return false;
    profiles.set(peerId, next);
    return true;
  }

  return {
    syncRoster(next) {
      if (!next.some((member) => member.id === hostId) || next.length > MAX_PLAYERS ||
          new Set(next.map((member) => member.id)).size !== next.length ||
          next.some((member) => !Number.isSafeInteger(member.id) || member.id < 1 ||
            !Number.isInteger(member.color) || member.color < 0 || member.color >= MAX_PLAYERS))
        return false;
      const changed = next.length !== members.length || next.some((member) =>
        !members.some((previous) => previous.id === member.id && previous.color === member.color));
      if (!changed) return false;
      for (const id of profiles.keys()) {
        if (!next.some((member) => member.id === id)) profiles.delete(id);
      }
      for (const member of next) {
        if (!profiles.has(member.id)) profiles.set(member.id, characterStyle(undefined, member.color));
      }
      members = next.map((member) => ({ ...member }));
      return true;
    },
    setOwn: (value) => update(hostId, value),
    receive(from, raw) {
      if (from === hostId || !members.some((member) => member.id === from)) return false;
      const message = readRoomMessage(raw, 'client');
      return message?.t === 'profile' && update(from, message.character);
    },
    get(peerId, fallbackColor = 0) {
      return { ...(profiles.get(peerId) ?? characterStyle(undefined, fallbackColor)) };
    },
  };
}
